import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Flag, Search, ShieldCheck } from "lucide-react";

import Modal from "react-modal";

import Spinner from "../../components/spinner";
import Pagination from "../../components/pagination";
import { useLanguage } from "../../context/languageContext";
import { Can } from "../../context/permissionContext";
import { commentsApi, formatWhen } from "../comments/commentsApi";
import PostPreview from "../comments/PostPreview";
import { useConfirm } from "../../components/confirmDialog";

/**
 * Reports — posts AND comments readers have flagged, newest first, in one
 * queue. `row.kind` says which; a comment row also carries the comment's words
 * and author, and its decision is recorded on that comment alone.
 *
 * A reader who reports a comment stops seeing it straight away, whatever is
 * decided here — this page decides whether anyone else should.
 *
 * One row per report, not per post: five people reporting the same thing is
 * five rows, because five people is the signal. The count travels with each
 * row so a single report and a pile-on do not look alike.
 *
 * Two answers, and the row keeps whichever was given: "Actioned" (the post was
 * taken down, edited, the author spoken to) and "Dismissed" (looked at, nothing
 * to do). Both mark the post's reports rather than deleting them, so a post
 * reported five times and cleared reads differently from one nobody ever saw.
 *
 * Taking the post down is still the Posts page's job, which is where the
 * permission for it lives — this records the decision.
 */

const STATUSES = [
  { value: "open", label: "Open" },
  { value: "actioned", label: "Actioned" },
  { value: "dismissed", label: "Dismissed" },
  { value: "", label: "All" },
];

/** The coloured word in the Status column. */
const StatusTag = ({ row }) => {
  if (row.status === "actioned") {
    return <span className="text-success">Actioned</span>;
  }
  if (row.status === "dismissed") {
    return <span className="text-muted">Dismissed</span>;
  }
  return <span className="text-warning">Open</span>;
};

/** Decisions on a comment are the Comments feature's, which can delete one. */
const decidePermission = (row) =>
  row.kind === "comment"
    ? { feature: "comments", action: "delete" }
    : { feature: "posts", action: "edit" };

/** A post's reports are decided together, a comment's on their own. */
const savingKey = (row) => (row.kind === "comment" ? row.commentId : row.postId);

const truncate = (text, max) => {
  const value = (text ?? "").trim();
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
};

const SEARCH_DELAY = 300;
const PAGE_SIZE = 25;

const Reports = () => {
  const { adminInfo } = useLanguage();
  const confirm = useConfirm();
  const api = useMemo(() => commentsApi(adminInfo?.token), [adminInfo?.token]);

  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [status, setStatus] = useState("open");
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    const id = setTimeout(() => {
      setQuery(search);
      setPage(1);
    }, SEARCH_DELAY);
    return () => clearTimeout(id);
  }, [search]);

  const load = useCallback(async () => {
    if (!adminInfo?.token) return;
    setLoading(true);
    try {
      const data = await api.reports({
        page,
        limit: PAGE_SIZE,
        search: query || undefined,
        status: status || undefined,
      });
      setRows(data?.items || []);
      setTotal(data?.total || 0);
      setPages(data?.pages || 1);
    } catch (error) {
      toast.error(error.message);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [adminInfo?.token, api, page, query, status]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (row, next) => {
    const isComment = row.kind === "comment";
    const title = isComment
      ? `the comment "${truncate(row.commentText, 60)}"`
      : `"${row.postTitle || "this post"}"`;
    const many = row.reportCount > 1 ? `${row.reportCount} reports` : "1 report";
    const ok = await confirm(
      next === "actioned"
        ? {
            title: "Mark these reports as actioned?",
            body: `${many} on ${title} will be marked as dealt with.`,
            confirmLabel: "Mark actioned",
            tone: "primary",
          }
        : {
            title: "Dismiss these reports?",
            body: `${many} on ${title} will be cleared. The ${isComment ? "comment" : "post"} stays up.`,
            confirmLabel: "Dismiss reports",
            tone: "neutral",
          }
    );
    if (!ok) return;

    setSaving(savingKey(row));
    try {
      if (isComment) await api.setCommentReportStatus(row.postId, row.commentId, next);
      else await api.setReportStatus(row.postId, next);
      toast.success(next === "actioned" ? "Marked as actioned" : "Reports dismissed");
      setViewing(null);
      await load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="p-3">
      <h4 className="mb-1">Reports {total > 0 ? `(${total})` : ""}</h4>
      <p className="text-muted small mb-3">
        Posts and comments readers have flagged. Dismissing clears the reports
        and leaves the content up; taking a post down is done from Posts, a
        comment from Comments. Whoever reported a comment already no longer
        sees it.
      </p>

      <div className="btn-group btn-group-sm mb-3" role="group">
        {STATUSES.map((option) => (
          <button
            key={option.value || "all"}
            type="button"
            className={`btn ${
              status === option.value ? "btn-dark" : "btn-outline-dark"
            }`}
            onClick={() => {
              setStatus(option.value);
              setPage(1);
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="position-relative mb-3">
        <Search
          size={16}
          className="position-absolute text-muted"
          style={{ left: 12, top: "50%", transform: "translateY(-50%)" }}
        />
        <input
          className="form-control form-control-sm ps-5"
          style={{ height: 40 }}
          placeholder="Search the reason given..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <div className="text-center text-muted py-5">
          <ShieldCheck size={28} className="mb-2" />
          <p className="mb-0">
            {query
              ? "No reports match that."
              : status === "open"
                ? "Nothing is waiting to be looked at."
                : "Nothing here."}
          </p>
        </div>
      ) : (
        <>
          <div className="table_scrollbar table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th className="index">S.no</th>
                  <th className="max_width">Reason</th>
                  <th>Reported by</th>
                  <th>On</th>
                  <th>Reported</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr
                    key={row._id}
                    onClick={() => setViewing(row)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{(page - 1) * PAGE_SIZE + i + 1}</td>
                    <td className="max_width">
                      {row.reason || <span className="text-muted">No reason given</span>}
                    </td>
                    <td>
                      {row.reporterName || (
                        <span className="text-muted">Deleted account</span>
                      )}
                      {row.reporterEmail ? (
                        <div className="text-muted small">{row.reporterEmail}</div>
                      ) : null}
                    </td>
                    <td>
                      {row.kind === "comment" ? (
                        <>
                          <span className="badge bg-secondary-subtle text-body me-1">Comment</span>
                          “{truncate(row.commentText, 120)}”
                          <div className="text-muted small">
                            by {row.commentAuthorName || "a deleted account"} · on{" "}
                            {row.postTitle || "an untitled post"}
                          </div>
                        </>
                      ) : (
                        row.postTitle || <span className="text-muted">Untitled</span>
                      )}
                      <div className="d-flex gap-2 align-items-center">
                        {row.reportCount > 1 ? (
                          <span className="badge bg-danger-subtle text-danger d-inline-flex align-items-center gap-1">
                            <Flag size={12} /> {row.reportCount} reports
                          </span>
                        ) : null}
                        {row.postVisible === false ? (
                          <span className="text-warning small">Hidden</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="text-nowrap">{formatWhen(row.reportedAt)}</td>
                    <td>
                      <StatusTag row={row} />
                      {row.reviewedBy ? (
                        <div className="text-muted small">by {row.reviewedBy}</div>
                      ) : null}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <Can {...decidePermission(row)}>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            disabled={saving === savingKey(row)}
                            onClick={() => decide(row, "actioned")}
                          >
                            Actioned
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={saving === savingKey(row)}
                            onClick={() => decide(row, "dismissed")}
                          >
                            Dismiss
                          </button>
                        </div>
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={pages}
              totalItems={total}
              itemsPerPage={PAGE_SIZE}
              onPageChange={setPage}
              showItemsPerPage={false}
              showTotalItems
            />
          )}
        </>
      )}
      {viewing && (
        <Modal
          isOpen={!!viewing}
          onRequestClose={() => setViewing(null)}
          className="modal_body_md"
          overlayClassName="modal_overlay"
        >
          {/* `modal_body_md` is a white panel from the light theme;
              `box_wrapper` paints it in the app's own surface colour. */}
          <div className="box_wrapper">
            <h5 className="mb-1">
              {viewing.kind === "comment" ? "Reported comment" : "Reported post"}
            </h5>
            <p className="text-muted small mb-3">
              {viewing.reason || "No reason given"} · {formatWhen(viewing.reportedAt)}
            </p>

            {viewing.kind === "comment" && (
              <blockquote className="border-start border-3 ps-3 mb-3">
                <p className="mb-1">“{viewing.commentText}”</p>
                <small className="text-muted">
                  {viewing.commentAuthorName || "A deleted account"}, on the post below
                </small>
              </blockquote>
            )}

            <PostPreview postId={viewing.postId} />

            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setViewing(null)}
              >
                Close
              </button>
              <Can {...decidePermission(viewing)}>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => decide(viewing, "dismissed")}
                >
                  Dismiss
                </button>
                <button
                  type="button"
                  className="btn btn-outline-success"
                  onClick={() => decide(viewing, "actioned")}
                >
                  Mark actioned
                </button>
              </Can>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Reports;
